import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Heading,
    Text,
    Divider,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {getStatusConfig, StatusDot} from '../../../utils/orderStatusConfig'
import {CopyIcon} from '../../../components/icons'
import {formatOrderDate} from '../../../utils/formateDate'
import {useCustomToast} from '../../../contexts/custom-toast-context'
import {useMultiStyleConfig, useBreakpointValue} from '@chakra-ui/react'
import {useBrand} from '../../../contexts/brand-context'

export const OrderInfo = ({order, headingRef}) => {
    const statusConfig = getStatusConfig(order && order.orderStatus)
    const {showCustomToast} = useCustomToast()
    const {formatMessage, locale} = useIntl()
    const styles = useMultiStyleConfig('OrderDetail')
    const {isGarage} = useBrand()

    const isOnlineOrder = order?.isOnlineOrder

    const handleCopyOrderNumber = async () => {
        const toastStyles = {
            position: 'fixed',
            top: '80px',
            right: {base: '0', lg: '24px'},
            left: {base: '0', lg: 'auto'},
            width: {base: '100%', lg: '400px'},
            maxWidth: {base: '100%', lg: '400px'},
            height: '44px',
            zIndex: 9999,
            fontSize: 'xs',
            fontWeight: 'normal',
            lineHeight: '18px'
        }

        try {
            await navigator.clipboard.writeText(order?.orderNo || '')
            showCustomToast({
                message: formatMessage({
                    id: 'account_order_detail.copy_success',
                    defaultMessage: 'Order number copied successfully.'
                }),
                styles: toastStyles
            })
        } catch (err) {
            showCustomToast({
                message: isGarage
                    ? formatMessage({
                          id: 'account_order_detail.copy_failed.garage',
                          defaultMessage: 'Failed to copy order number. Please try again.'
                      })
                    : formatMessage({
                          id: 'account_order_detail.copy_failed.dynamite',
                          defaultMessage: 'Failed to copy order number. Please try again.'
                      }),
                styles: toastStyles
            })
        }
    }
    return (
        <>
            <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
                <Text
                    {...styles.orderDetailHeading}
                    tabIndex="0"
                    ref={headingRef}
                    data-testid="order-details-heading"
                >
                    {isOnlineOrder ? (
                        <FormattedMessage
                            defaultMessage="Order Details"
                            id="account_order_detail.title.order_details"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Purchase Details"
                            id="account_order_detail.title.purchase_details"
                        />
                    )}
                </Text>

                <Flex flexDir={'column'}>
                    <Box display={'flex'} flexDir={'column'} gap={'10px'}>
                        <Text {...styles.orderStatusText}>
                            {isOnlineOrder ? (
                                <FormattedMessage
                                    defaultMessage="Order Status"
                                    id="account_order_detail.label.order_status_label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Status"
                                    id="account_order_detail.label.pos_status_label"
                                />
                            )}
                        </Text>

                        <Flex gap={'2px'}>
                            <StatusDot
                                color={statusConfig.color}
                                animated={statusConfig.animated}
                            />
                            <Text
                                {...styles.orderStatusInfoText}
                                data-testid="order-details-text-status"
                            >
                                {order && order.orderStatus}
                            </Text>
                        </Flex>
                    </Box>
                    <Divider w={'full'} h={'1px'} background={'gray.50'} m={'16px 0'} />
                    <Box display={'flex'} flexDir={'column'} gap={'8px'}>
                        <Heading as={'h3'} {...styles.orderNumberText}>
                            {isOnlineOrder ? (
                                <FormattedMessage
                                    defaultMessage="Order Number"
                                    id="account_order_detail.label.order_number_label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Transaction Number"
                                    id="account_order_detail.label.transaction_number_label"
                                />
                            )}
                        </Heading>
                        <Flex gap={'2px'}>
                            <Text
                                {...styles.orderNuberValue}
                                data-testid="order-details-text-order-number"
                                data-private
                            >
                                {order && order.orderNo}
                            </Text>
                            <Button
                                variant="unstyled"
                                onClick={handleCopyOrderNumber}
                                data-testid="order-details-copy-order-number"
                                aria-label="Copy order number"
                                minW="auto"
                                height={'auto'}
                                ml={'4px'}
                                mt={'-2px'}
                                p="0"
                            >
                                <CopyIcon boxSize={'20px'} />
                            </Button>
                        </Flex>
                    </Box>
                    <Divider w={'full'} h={'1px'} background={'gray.50'} m={'16px 0'} />
                    <Box display={'flex'} flexDir={'column'} gap={'8px'}>
                        <Heading as={'h3'} {...styles.OrderDateText}>
                            {isOnlineOrder ? (
                                <FormattedMessage
                                    defaultMessage="Order Date"
                                    id="account_order_detail.label.order_date_label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Purchase Date"
                                    id="account_order_detail.label.purchase_date_label"
                                />
                            )}
                        </Heading>
                        <Text
                            {...styles.orderDateValue}
                            data-testid="order-details-text-order-date"
                        >
                            {formatOrderDate(order && order.orderDate, locale)}
                        </Text>
                    </Box>
                </Flex>
            </Box>
        </>
    )
}

OrderInfo.propTypes = {
    order: PropTypes.shape({
        orderNo: PropTypes.string,
        orderDate: PropTypes.string,
        orderStatus: PropTypes.string
    }),
    headingRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})])
}
